// Global Instructions Rule Applied!
//
// Configuration → Email Categories (admin only).
//
// The taxonomy that decides how every inbound email is filed, and the settings
// that drive it. Until this panel existed the only way to change a precedence,
// rename a category, or flip a categorization toggle was a hand-written SQL
// migration — which is a poor fit for a taxonomy whose whole point is to be
// tuned against real mail during the Power Automate parallel run.
//
// Three sections, in the order an admin reasons about them:
//   1. Category taxonomy — cfg.email_categories. Precedence order (the
//      resolution model itself), labels, the Outlook name + colour, and the
//      per-category kill switch.
//   2. Categorization    — cfg.app_config group 'email_categorization'. Master switch,
//      the LLM tier, batch size, the daily model spend cap, and the labels that
//      mean "declined" for a fundraising prospect.
//   3. Outlook write-back — the switch that lets Reservoir stamp categories
//      into people's mailboxes, plus its batch size and colour sync.
//   4. Recipient markers — cfg.email_recipient_markers. The additive "To" /
//      "CC" category stamped ALONGSIDE the content category. A separate
//      dimension, hence its own section rather than rows in the taxonomy
//      table: the taxonomy is mutually exclusive and precedence-ordered,
//      whereas a message is "Pipeline" AND "To".
//
// NO ADD, NO DELETE, deliberately. category_key drives the hardcoded predicates
// inside email_classification__classify_event: a row invented here would have
// no predicate and could never match an email. `Enabled` is the supported way
// to take a category out of play — the resolver already honours it, because the
// final pick is the min-precedence ENABLED row among the matches.
//
// Chrome copied from InternalAgentsPanel.tsx (the newest design-pattern-
// compliant sibling): FormSection + AntD Table inside it, per-field autosave on
// blur/change, the standard Tag vocabulary.
//
// ERRORS RENDER INLINE, not only as a toast. AntD's static `message` does not
// render anywhere in this app (antd 5.27 on React 19 without the compat patch —
// see #239's known issues), and every refusal here is a DESIGNED outcome the
// admin has to read: an out-of-range batch size, a partial reorder, a bad
// colour. So the panel keeps the last refusal in an Alert and reloads, which
// also snaps a refused Switch back to what the database actually says.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Input, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { ArrowDownOutlined, ArrowUpOutlined, ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import InlineNumberField from './InlineNumberField.tsx'
import {
  EmailCategoriesConfigAdminData,
  EmailCategoryAdminRow,
  EmailRecipientMarker,
  OUTLOOK_PRESETS
} from '../../../state_manager/data_models/email_categories_config_model.ts'
import {
  fetchEmailCategoriesConfig,
  reorderEmailCategories,
  updateEmailCategory,
  updateEmailCategorySetting,
  updateEmailRecipientMarker
} from '../services/emailCategoriesConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const EMPTY: EmailCategoriesConfigAdminData = {
  resolvedEnvironment: 'global',
  categories: [],
  settings: [],
  recipientMarkers: []
}

/** How each marker is chosen, in the admin's language. A mailbox on BOTH lines
 *  is a To recipient — resolved in email_writeback__enqueue, stated here so
 *  the panel does not imply a message can carry both. */
const MARKER_HINT: Record<string, string> = {
  to: 'Stamped when this mailbox is on the To line. A mailbox on both To and Cc counts as To.',
  cc: 'Stamped when this mailbox is on the Cc line only.'
}

/** How the category resolves before any model call. Read-only — it describes
 *  the code, not a setting. Standard Tag vocabulary: reference = blue,
 *  needs-attention (costs a model call) = gold, fallback = default. */
const DERIVATION_TAG: Record<string, { color: string; hint: string }> = {
  deterministic: {
    color: 'blue',
    hint: 'Resolved from the data at trigger time — domains, roles, commitments, open deals. No model call.'
  },
  hybrid: {
    color: 'gold',
    hint: 'Part deterministic, part model verdict: the sender is matched from the data, "is this a proposal" is asked of the model.'
  },
  llm: { color: 'gold', hint: 'Decided by the model tier.' },
  fallback: { color: 'default', hint: 'What an email is filed as when nothing else matched.' }
}

const presetHex = (preset?: string | null): string | undefined =>
  OUTLOOK_PRESETS.find((p) => p.value === preset)?.hex

function Swatch({ preset }: { preset?: string | null }): React.ReactElement | null {
  const hex = presetHex(preset)
  if (!hex) return null
  return (
    <span
      className='inline-block rounded-sm border border-gray-300'
      style={{ width: 12, height: 12, backgroundColor: hex }}
    />
  )
}

export default function EmailCategoriesPanel(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [data, setData] = useState<EmailCategoriesConfigAdminData>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchEmailCategoriesConfig(token()))
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Failed to load the email category configuration'
      setRefusal(text)
      showErrorToast(text)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  /** Every write goes through here: clear the last refusal, act, reload. On
   *  failure keep the message visible AND reload, so a control the server
   *  refused snaps back to the stored value instead of showing the rejected
   *  one. */
  const run = async (action: () => Promise<void>, failure: string): Promise<boolean> => {
    setRefusal(null)
    try {
      await action()
      await load()
      return true
    } catch (e) {
      const text = e instanceof Error ? e.message : failure
      setRefusal(text)
      showErrorToast(text)
      await load()
      return false
    }
  }

  const saveField = (row: EmailCategoryAdminRow, fields: Record<string, unknown>, failure: string): void => {
    run(() => updateEmailCategory(token(), row.email_categories__id, fields), failure)
  }

  const saveSetting = (key: string, value: boolean | number | string[], failure: string): void => {
    run(() => updateEmailCategorySetting(token(), key, value), failure)
  }

  // ---- settings -----------------------------------------------------------
  const settings = useMemo(() => {
    const byKey: Record<string, unknown> = {}
    data.settings.forEach((s) => {
      byKey[s.app_config__config_key] = s.app_config__value_json
    })
    return byKey
  }, [data.settings])

  // Declined statuses are cfg.pipeline_statuses IDS (via the synthesized
  // lookup groups). Fundraising statuses drive the prospect-stage match;
  // the origination group rides along because the historical label set
  // ('Passed', 'Cancelled', …) lives there.
  const { getLookupOptions } = useLookups()
  const declinedStatusOptions = useMemo(
    () => [
      { label: 'Fundraising', options: getLookupOptions('Fundraising Status') },
      { label: 'Deal Origination', options: getLookupOptions('Deal Origination Status') }
    ],
    [getLookupOptions]
  )

  const shadowed = useMemo(
    () => data.settings.some((s) => s.app_config__is_environment_override),
    [data.settings]
  )

  const writebackOn = settings.outlook_writeback_enabled === true

  // Names of the markers currently being stamped, for the section tag. Empty
  // when none are on — which is also the state while write-back itself is off,
  // since nothing is written at all then.
  const markersOn = useMemo(
    () =>
      data.recipientMarkers
        .filter((m) => m.email_recipient_markers__enabled)
        .map((m) => m.email_recipient_markers__label),
    [data.recipientMarkers]
  )

  // ---- precedence reorder -------------------------------------------------
  // The RPC takes the WHOLE order and refuses a partial list, so a move is
  // expressed as a swap over the current order rather than a per-row write.
  const move = (index: number, delta: number): void => {
    const ids = data.categories.map((c) => c.email_categories__id)
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    run(() => reorderEmailCategories(token(), ids), 'Failed to reorder the categories')
  }

  const columns = [
    {
      title: 'Order',
      width: 78,
      render: (_: unknown, row: EmailCategoryAdminRow, index: number) => (
        <span className='flex items-center gap-1'>
          <Button
            size='small'
            type='text'
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => move(index, -1)}
          />
          <Button
            size='small'
            type='text'
            icon={<ArrowDownOutlined />}
            disabled={index === data.categories.length - 1}
            onClick={() => move(index, 1)}
          />
        </span>
      )
    },
    {
      title: 'Category',
      dataIndex: 'email_categories__label',
      render: (v: string, row: EmailCategoryAdminRow) => (
        <div>
          <Input
            size='small'
            defaultValue={v}
            key={`${row.email_categories__id}-${v}`}
            onBlur={(e) => {
              const next = e.target.value.trim()
              if (next && next !== v) saveField(row, { label: next }, 'Failed to rename the category')
            }}
            style={{ width: 150 }}
          />
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {row.email_categories__category_key}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: (
        <Tooltip title='How the category is decided before any model call. Set by the resolver, not editable.'>
          Derivation
        </Tooltip>
      ),
      dataIndex: 'email_categories__derivation',
      width: 130,
      render: (v: string) => {
        const spec = DERIVATION_TAG[v] || { color: 'default', hint: '' }
        return (
          <Tooltip title={spec.hint}>
            <Tag color={spec.color}>{v}</Tag>
          </Tooltip>
        )
      }
    },
    {
      title: 'Description',
      dataIndex: 'email_categories__description',
      render: (v: string | null, row: EmailCategoryAdminRow) => (
        <Input
          size='small'
          defaultValue={v ?? ''}
          key={`${row.email_categories__id}-desc-${v ?? ''}`}
          onBlur={(e) => {
            const next = e.target.value.trim()
            if (next !== (v ?? '')) saveField(row, { description: next }, 'Failed to update the description')
          }}
        />
      )
    },
    {
      title: (
        <Tooltip title="The Outlook category name the Power Automate flow stamps. Used only to score the parallel-run comparison — Reservoir never writes this value.">
          Automate label
        </Tooltip>
      ),
      dataIndex: 'email_categories__automate_label',
      width: 150,
      render: (v: string | null, row: EmailCategoryAdminRow) => (
        <Input
          size='small'
          defaultValue={v ?? ''}
          key={`${row.email_categories__id}-auto-${v ?? ''}`}
          placeholder='—'
          onBlur={(e) => {
            const next = e.target.value.trim()
            if (next !== (v ?? '')) saveField(row, { automate_label: next }, 'Failed to update the Automate label')
          }}
        />
      )
    },
    {
      title: (
        <Tooltip title='The name Reservoir stamps on the Outlook message. Leave blank to use the Automate label, or the category name if that is blank too.'>
          Outlook label
        </Tooltip>
      ),
      dataIndex: 'email_categories__outlook_label',
      width: 170,
      render: (v: string | null, row: EmailCategoryAdminRow) => (
        <div>
          <Input
            size='small'
            defaultValue={v ?? ''}
            key={`${row.email_categories__id}-out-${v ?? ''}`}
            placeholder={row.email_categories__effective_outlook_label ?? '—'}
            onBlur={(e) => {
              const next = e.target.value.trim()
              if (next !== (v ?? '')) saveField(row, { outlook_label: next }, 'Failed to update the Outlook label')
            }}
          />
          {!v && (
            <Text type='secondary' style={{ fontSize: 12 }}>
              stamps “{row.email_categories__effective_outlook_label}”
            </Text>
          )}
        </div>
      )
    },
    {
      title: (
        <Tooltip title="Outlook's fixed palette. The colour is applied to the mailbox's master category list, which is what makes the stamp show in colour rather than plain grey.">
          Colour
        </Tooltip>
      ),
      dataIndex: 'email_categories__outlook_color',
      width: 160,
      render: (v: string | null, row: EmailCategoryAdminRow) => (
        <Select
          size='small'
          style={{ width: 140 }}
          value={v ?? undefined}
          placeholder='None'
          allowClear
          onChange={(next) =>
            saveField(row, { outlook_color: next ?? '' }, 'Failed to update the colour')
          }
          options={OUTLOOK_PRESETS.map((p) => ({
            value: p.value,
            label: (
              <span className='flex items-center gap-2'>
                <Swatch preset={p.value} />
                {p.label}
              </span>
            )
          }))}
        />
      )
    },
    {
      title: (
        <Tooltip title='Emails filed under this category in the last 30 days. A category stuck on zero is either genuinely rare or outranked by one above it.'>
          30 days
        </Tooltip>
      ),
      dataIndex: 'email_categories__classified_count_30d',
      width: 90,
      align: 'right' as const,
      render: (v: number | null) => (
        <Text type={v ? undefined : 'secondary'}>{Number(v ?? 0).toLocaleString()}</Text>
      )
    },
    {
      title: (
        <Tooltip title='A disabled category is skipped when picking the winner, so its emails fall through to the next matching category down the order.'>
          Enabled
        </Tooltip>
      ),
      dataIndex: 'email_categories__enabled',
      width: 90,
      render: (v: boolean | null, row: EmailCategoryAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) => saveField(row, { enabled: checked }, 'Failed to update the category')}
        />
      )
    }
  ]

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {refusal && (
        <Alert
          type='error'
          showIcon
          closable
          message={refusal}
          onClose={() => setRefusal(null)}
        />
      )}

      <FormSection
        title='Category taxonomy'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Highest first — an email is filed as the topmost enabled category that matches it
            </Text>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='email_categories__id'
          pagination={false}
          dataSource={data.categories}
          columns={columns}
        />
        <Text type='secondary' style={{ fontSize: 12 }}>
          Categories cannot be added or removed here: each one is matched by a rule built into the
          resolver, so a new row would never match an email. Disable one instead to take it out of
          play.
        </Text>
      </FormSection>

      <FormSection
        title='Categorization'
        titleColor='green'
        scrollable={false}
        loading={loading}
        headerActions={
          shadowed ? (
            <Tag color='purple'>Overridden for {data.resolvedEnvironment}</Tag>
          ) : null
        }
      >
        <div className='flex flex-col gap-3 max-w-[560px]'>
          <div className='flex items-center justify-between'>
            <Text>Categorize inbound email</Text>
            <Switch
              size='small'
              checked={settings.categorization_enabled !== false}
              onChange={(checked) =>
                saveSetting('categorization_enabled', checked, 'Failed to update the master switch')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Off = email is still captured, but nothing is categorized.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Use the model for ambiguous email</Text>
            <Switch
              size='small'
              checked={settings.categorization_llm_enabled !== false}
              onChange={(checked) =>
                saveSetting('categorization_llm_enabled', checked, 'Failed to update the model tier')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Warm Inbound, Cold Inbound and Spam need a model verdict. Off = those emails wait rather
            than being filed wrongly.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Emails per model call</Text>
            <InlineNumberField
              value={(settings.classification_batch_size as number) ?? null}
              min={1}
              max={200}
              width={120}
              onCommit={(v) =>
                v !== null && saveSetting('classification_batch_size', v, 'Failed to update the batch size')
              }
            />
          </div>

          <div className='flex items-center justify-between'>
            <Text>Max model spend per day ($)</Text>
            <InlineNumberField
              value={(settings.classification_max_cost_per_day_usd as number) ?? null}
              min={0.01}
              max={1000}
              width={120}
              onCommit={(v) =>
                v !== null &&
                saveSetting('classification_max_cost_per_day_usd', v, 'Failed to update the spend cap')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Its own pool, separate from the internal agents, summed since 00:00 UTC. Roughly $0.0005
            per email reaches the model.
          </Text>

          <div className='flex flex-col gap-1'>
            <Text>Automated senders</Text>
            <Select
              size='small'
              mode='tags'
              className='w-full'
              placeholder='noreply, mailer-daemon…'
              value={(settings.noreply_sender_patterns as string[]) ?? []}
              onChange={(next) =>
                saveSetting('noreply_sender_patterns', next, 'Failed to update the automated-sender list')
              }
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              Matched against the part of the address before the @, so “noreply” also catches
              “microsoft-noreply@”. These senders still count as external, but are not matched to an
              account — otherwise a vendor's automated mail inherits that vendor's role, and a
              Microsoft invoice gets filed as Co-investor.
            </Text>
          </div>

          <div className='flex flex-col gap-1'>
            <Text>Statuses that mean an LP declined</Text>
            <Select
              size='small'
              mode='multiple'
              className='w-full'
              placeholder='Select statuses'
              value={(settings.declined_decision_statuses as string[]) ?? []}
              options={declinedStatusOptions}
              optionFilterProp='label'
              onChange={(next) =>
                saveSetting('declined_decision_statuses', next, 'Failed to update the declined statuses')
              }
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              Decision statuses (cfg-managed) whose stage means a prospect declined. They separate
              Declined LP from Target LP. Stored as status ids, so renaming a status no longer
              breaks the match.
            </Text>
          </div>
        </div>
      </FormSection>

      <FormSection
        title='Outlook write-back'
        titleColor='orange'
        scrollable={false}
        loading={loading}
        headerActions={
          writebackOn ? <Tag color='green'>Stamping Outlook</Tag> : <Tag color='default'>Off</Tag>
        }
      >
        <div className='flex flex-col gap-3 max-w-[560px]'>
          <Alert
            type={writebackOn ? 'warning' : 'info'}
            showIcon
            message={
              writebackOn
                ? 'Reservoir is writing categories into monitored mailboxes. The Power Automate flow must be switched off — two systems stamping the same message will fight.'
                : 'Categories are recorded in Reservoir but nothing is written to Outlook. Turn this on only after the Power Automate comparison is accepted and that flow is switched off.'
            }
          />

          <div className='flex items-center justify-between'>
            <Text>Stamp the category on the Outlook message</Text>
            <Switch
              size='small'
              checked={writebackOn}
              onChange={(checked) =>
                saveSetting('outlook_writeback_enabled', checked, 'Failed to update write-back')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Applies to every monitored mailbox on the email — To and Cc alike. Categories people set
            themselves are left alone.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Keep mailbox colours in sync</Text>
            <Switch
              size='small'
              checked={settings.writeback_sync_master_categories !== false}
              onChange={(checked) =>
                saveSetting(
                  'writeback_sync_master_categories',
                  checked,
                  'Failed to update the colour sync'
                )
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Adds each category and its colour to the mailbox's own category list. Off = the category
            still applies, but Outlook shows it without a colour.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Messages per write-back pass</Text>
            <InlineNumberField
              value={(settings.writeback_batch_size as number) ?? null}
              min={1}
              max={200}
              width={120}
              onCommit={(v) =>
                v !== null && saveSetting('writeback_batch_size', v, 'Failed to update the batch size')
              }
            />
          </div>
        </div>
      </FormSection>

      <FormSection
        title='Recipient markers'
        titleColor='orange'
        scrollable={false}
        loading={loading}
        headerActions={
          markersOn.length > 0
            ? <Tag color='green'>{markersOn.join(' + ')}</Tag>
            : <Tag color='default'>Off</Tag>
        }
      >
        <div className='flex flex-col gap-3'>
          <Text type='secondary' style={{ fontSize: 12 }}>
            An extra category recording how the mailbox was addressed, stamped alongside the content
            category — so a message can read “Pipeline” and “To”. Switching one off removes it from
            the next message the write-back re-stamps.
          </Text>

          <Table<EmailRecipientMarker>
            rowKey='email_recipient_markers__id'
            size='small'
            pagination={false}
            loading={loading}
            dataSource={data.recipientMarkers}
            columns={[
              {
                title: 'Line',
                dataIndex: 'email_recipient_markers__marker_key',
                width: 90,
                render: (v: string) => (
                  <Tooltip title={MARKER_HINT[v]}>
                    <Text strong>{v === 'to' ? 'To' : 'Cc'}</Text>
                  </Tooltip>
                )
              },
              {
                title: (
                  <Tooltip title='The Outlook category name written onto the message. Renaming it strips the old name on the next re-stamp.'>
                    Category name
                  </Tooltip>
                ),
                dataIndex: 'email_recipient_markers__label',
                width: 200,
                render: (v: string, row: EmailRecipientMarker) => (
                  <Input
                    size='small'
                    defaultValue={v}
                    onBlur={(e) => {
                      const next = e.target.value.trim()
                      if (next && next !== v) {
                        run(
                          () =>
                            updateEmailRecipientMarker(
                              token(),
                              row.email_recipient_markers__marker_key,
                              { label: next }
                            ),
                          'Failed to update the marker name'
                        )
                      }
                    }}
                  />
                )
              },
              {
                title: 'Colour',
                dataIndex: 'email_recipient_markers__outlook_color',
                width: 160,
                render: (v: string | null, row: EmailRecipientMarker) => (
                  <Select
                    size='small'
                    style={{ width: 140 }}
                    value={v ?? undefined}
                    placeholder='None'
                    allowClear
                    onChange={(next) =>
                      run(
                        () =>
                          updateEmailRecipientMarker(
                            token(),
                            row.email_recipient_markers__marker_key,
                            { outlook_color: next ?? null }
                          ),
                        'Failed to update the marker colour'
                      )
                    }
                    options={OUTLOOK_PRESETS.map((p) => ({
                      value: p.value,
                      label: (
                        <span className='flex items-center gap-2'>
                          <Swatch preset={p.value} />
                          {p.label}
                        </span>
                      )
                    }))}
                  />
                )
              },
              {
                title: 'Stamped',
                dataIndex: 'email_recipient_markers__enabled',
                width: 100,
                render: (v: boolean, row: EmailRecipientMarker) => (
                  <Switch
                    size='small'
                    checked={!!v}
                    onChange={(checked) =>
                      run(
                        () =>
                          updateEmailRecipientMarker(
                            token(),
                            row.email_recipient_markers__marker_key,
                            { enabled: checked }
                          ),
                        'Failed to update the marker'
                      )
                    }
                  />
                )
              }
            ]}
          />

          <Text type='secondary' style={{ fontSize: 12 }}>
            Only mail classified from now on picks this up. To add a marker to mail that is already
            stamped, run the write-back backfill over the period with “include already-queued”.
          </Text>
        </div>
      </FormSection>
    </div>
  )
}
